<template>
	<JBox>
		<!-- ###################### -->
		<!-- Normal Layout -->
		<!-- ###################### -->
		<JBox class="s-px-8">
			<!-- Algo -->
			<JBox class="s-bg-white s-p-6 s-shadow s-rounded-lg s-mt-6 s-max-w-5xl s-mx-auto">
				<JHeadline class="s-text-lg s-text-gray-600 s-mb-4">Logic</JHeadline>
				<JBox>
					<CCodeEditor language="json" :value="value" @input="$emit('input', $event)" />
				</JBox>
				<JFlex class="s-mt-4 s-justify-between">
					<JButton variant="white-sm" @click="switchLayout('fullscreen')">Fullscreen mode</JButton>
					<JButton variant="white-sm" @click="formatJsonLogic">Format</JButton>
				</JFlex>
			</JBox>

			<!-- Test -->
			<JBox class="s-bg-white s-p-6 s-shadow s-rounded-lg s-mt-10 s-max-w-5xl s-mx-auto">
				<JHeadline class="s-text-lg s-text-gray-600 s-mb-4">Test with variables</JHeadline>
				<JBox>
					<CCodeEditor language="json" v-model="variables" />
				</JBox>

				<!-- Result -->
				<JBox v-if="testResult !== null" class="s-mt-2 s-p-4 s-bg-yellow-100 s-border s-border-yellow-200 s-rounded-lg">
					Test Result: {{ testResult }}
				</JBox>

				<!-- Button -->
				<JFlex class="s-mt-2 s-justify-between">
					<JButton variant="default-light-sm" @click="testJsonLogic">Test</JButton>
					<JButton variant="default-light-sm" @click="extractJsonLogicVariables">Extract Variables</JButton>
				</JFlex>
			</JBox>
		</JBox>

		<!-- ###################### -->
		<!-- Fullscreen Layout -->
		<!-- ###################### -->
		<JBox v-if="layout === 'fullscreen'" class="s-w-screen s-h-screen s-overflow-y-hidden s-fixed s-top-0 s-left-0 s-bg-white">
			<CCodeEditor language="json" :value="value" @input="$emit('input', $event)" height="100vh" />

			<!-- Test section -->
			<JBox
				class="draggable s-bg-white s-p-4 s-fixed s-bottom-10 s-right-10 s-rounded-lg"
				:style="
					`width:550px; touch-action: none; transform: translate(${variablesDragPosition.x}px,${variablesDragPosition.y}px)`
				"
			>
				<JFlex class="s-items-center s-mb-2">
					<JFlex class="s-w-1/2 s-items-center">
						<JHeadline class="s-text-sm s-text-gray-900">Test with variables</JHeadline>
					</JFlex>
					<JFlex class="s-w-1/2 s-justify-end">
						<JButton variant="default-light-xs" class="s-mr-2" @click="testJsonLogic">Test</JButton>
						<JButton variant="default-light-xs" @click="extractJsonLogicVariables">Extract Variables</JButton>
					</JFlex>
				</JFlex>
				<JBox v-if="testResult !== null" class="s-text-xs s-mb-2 s-p-2 s-bg-yellow-100 s-border s-border-yellow-200">
					Test Result: {{ testResult }}
				</JBox>
				<CCodeEditor language="json" v-model="variables" />
			</JBox>

			<!-- Close -->
			<JFlex
				class="s-fixed s-top-4 s-right-4 s-w-9 s-h-9 s-bg-gray-100 s-rounded-full s-items-center s-justify-center s-cursor-pointer s-mr-2"
				@click="switchLayout('normal')"
			>
				<CIcon name="x" class="s-w-4 s-h-4 s-text-gray-500 s-fill-current" />
			</JFlex>
		</JBox>
	</JBox>
</template>

<script>
import jsonLogic from "json-logic-js"
import interact from "interactjs"
import set from "lodash.set"

export default {
	props: {
		// json logic
		value: {
			type: String
		}
	},

	data() {
		return {
			layout: "normal", // normal, fullscreen

			// Test json logic
			variables: "",
			variablesDragPosition: { x: 0, y: 0 }, // persist coordinates for variable window in fullscreen layout
			testResult: null
		}
	},

	methods: {
		switchLayout(layout) {
			this.layout = layout
			if (layout === "fullscreen") this.initInteract()
		},

		testJsonLogic() {
			let rules, data
			try {
				rules = JSON.parse(this.value) ?? {}
				data = JSON.parse(this.variables) ?? {}
			} catch (err) {
				this.testResult = `Oops, there is a problem with your json logic, ${err}` // TODO: wrefactor should come from locales
				return
			}

			this.testResult = jsonLogic.apply(rules, data)
		},

		extractJsonLogicVariables() {
			const extractPropertyValues = (obj, property) => {
				const traverse = (obj, property) => {
					for (const i in obj) {
						if (Array.isArray(obj[i]) || typeof obj[i] === "object") traverse(obj[i], property)
						else {
							if (obj[property] !== undefined) foundPropertyValues.push(obj[property])
						}
					}
				}

				const foundPropertyValues = []
				traverse(obj, property)
				return foundPropertyValues
			}

			// Parse safely
			let parsedJsonLogic
			try {
				parsedJsonLogic = JSON.parse(this.value)
			} catch (err) {
				this.testResult = `Oops, there is a problem with your json logic, ${err}` // TODO: wrefactor should come from locales
				return
			}

			const property = "var" // look for all vars in jsonLogic object
			const variablePaths = extractPropertyValues(parsedJsonLogic, property) // will return array of varaibles, can be e,g, "value" or "value.value2"

			// Convert from paths into one JSON object
			const output = {}
			variablePaths.forEach(variablePath => set(output, variablePath, null))

			// Assign
			this.variables = JSON.stringify(output, null, 2)
		},

		formatJsonLogic() {
			let output
			try {
				output = JSON.stringify(JSON.parse(this.value), null, 2)
			} catch (e) {
				return
			}

			this.$emit("input", output)
		},

		initInteract() {
			interact(".draggable").draggable({
				listeners: {
					move: event => {
						this.variablesDragPosition.x += event.dx
						this.variablesDragPosition.y += event.dy
						event.target.style.transform = `translate(${this.variablesDragPosition.x}px, ${this.variablesDragPosition.y}px)`
					}
				}
			})
		}
	}
}
</script>
